import {
  setPlumaContextValue,
  setPlumaTheme,
  setPlumaIconManager,
  plumaContextValueStore as legacyPlumaContextValueStore,
  plumaThemeStore as legacyPlumaThemeStore,
  plumaIconManagerStore as legacyPlumaIconManagerStore,
} from '@customerio/hydra-pluma-state';
import { IconManager } from '@customerio/pluma-icons';
import { defaultTheme, newTheme, darkTheme } from '@customerio/pluma-components/css';
import type { PlumaTheme } from '@customerio/pluma-components/css';
import { PlumaProvider, GlobalThemeManager } from '@customerio/pluma-components/react';
import type { PlumaContextValue } from '@customerio/pluma-components/react';
import { atom } from 'nanostores';
import { useStore } from '@nanostores/react';
import { forwardRef, useLayoutEffect, useEffect } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import type { LinkComponent, PlumaProviderProps } from '@customerio/pluma-components/react';
import type { TargetTransitionOptions } from '@/services/router/safe-navigate';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { shouldProcessLinkClick } from '../../utils/navigation';
import { appendMasqParam } from '@/utils/masq';
import { isEmptyString } from '@/utils/string';
import { useThemeContext } from '@/context/theme';
import merge from 'lodash-es/merge';

const iconManager = new IconManager();
setPlumaIconManager(iconManager);

const UNSAFE_ICON_SIZE_OVERRIDE_LS_KEY = 'hydra:unsafe_icon_size_override';
let defaultIconSizeVersion: 'v1' | 'v2' = 'v2';
const iconSizeOverride = localStorage.getItem(UNSAFE_ICON_SIZE_OVERRIDE_LS_KEY);
if (iconSizeOverride != null && (iconSizeOverride === 'v1' || iconSizeOverride === 'v2')) {
  defaultIconSizeVersion = iconSizeOverride;
}

const globalThemeManager = new GlobalThemeManager();

export const plumaContextValueStore = atom<Partial<PlumaContextValue> | null>({
  componentFlags: {
    unsafe_useLegacyButton: true,
    unsafe_useLegacyTextField: true,
    unsafe_useLegacyTextArea: true,
    unsafe_useLegacyLink: true,
  },
  componentConfig: {
    PlumaIcon: {
      defaultSizeVersion: defaultIconSizeVersion,
    },
  },
  iconManager,
  globalThemeManager,
});

// TODO: Remove hydra-pluma-state once child apps are updated to use the new stores
setPlumaContextValue(plumaContextValueStore.get() ?? {});
legacyPlumaIconManagerStore.subscribe((value) => {
  plumaContextValueStore.set(merge({}, plumaContextValueStore.get(), { iconManager: value as unknown as IconManager }));
});
legacyPlumaContextValueStore.subscribe((value) => {
  plumaContextValueStore.set(merge({}, plumaContextValueStore.get(), value));
});

export const plumaThemeStore = atom<PlumaTheme | null>(defaultTheme);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
setPlumaTheme(plumaThemeStore.get() as any);
legacyPlumaThemeStore.subscribe((value) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  plumaThemeStore.set(value as any);
});

/**
 * PlumaLinkDefinition connects the Pluma design system to the
 * react-router-dom Link component. It allows for the use of
 * react-router-dom's navigation features while still using
 * Pluma's styling and theming.
 */
const PlumaLinkDefinition = forwardRef(function (props, ref) {
  const { href, replace, onClick, ...restProps } = props as any;

  const safeNavigate = useSafeNavigate();

  // Automatically append masq param if masquerading
  const finalHref = !isEmptyString(href) ? appendMasqParam(href) : '';

  const onLinkClick = (event: React.MouseEvent<HTMLAnchorElement>, options?: TargetTransitionOptions) => {
    onClick?.(event);

    // Check if the consumer's onClick handler prevented the default behavior
    if (event.defaultPrevented) {
      return;
    }

    if (isEmptyString(finalHref)) {
      event.preventDefault();
      return;
    }

    if (!shouldProcessLinkClick(event)) {
      return;
    }

    // Let browser handle external links (mailto:, tel:, http://, https://)
    const targetHref = event.currentTarget.getAttribute('href') ?? '';
    if (/^(mailto:|tel:|https?:\/\/)/.test(targetHref)) {
      return; // Let browser handle it naturally
    }

    event.preventDefault();
    event.stopPropagation();

    safeNavigate(targetHref, options);
  };

  return <ReactRouterLink to={finalHref} replace={replace} {...restProps} onClick={onLinkClick} ref={ref} />;
}) as LinkComponent;

/**
 * Configures Pluma for Root and EmbedRoot components,
 * set globally so that other apps can consume our configuration.
 */
export function ConnectedPlumaProvider(props: PlumaProviderProps) {
  const plumaContextValue = useStore(plumaContextValueStore);
  const plumaTheme = useStore(plumaThemeStore);
  const themeContext = useThemeContext();

  useLayoutEffect(() => {
    if (themeContext.isDarkMode) {
      plumaThemeStore.set(darkTheme);
      setPlumaTheme(darkTheme as any);
    } else {
      plumaThemeStore.set(newTheme);
      setPlumaTheme(newTheme as any);
    }
  }, [themeContext.isDarkMode]);

  const resolvedTheme = plumaTheme ?? (themeContext.isDarkMode ? darkTheme : undefined) ?? newTheme;

  useEffect(() => {
    let newValue = merge({}, plumaContextValueStore.get());
    let hasUpdated = false;

    if (plumaContextValue?.componentConfig?.PlumaIcon?.iconNameOverrides?.['design-studio'] !== 'design-studio-new') {
      newValue = merge(newValue, {
        componentConfig: {
          PlumaIcon: {
            iconNameOverrides: {
              'design-studio': 'design-studio-new',
            },
          },
        },
      });
      hasUpdated = true;
    }

    if (plumaContextValue?.componentConfig?.PlumaButton?.unsafe_withSoftDeprecatedSecondaryVariant !== true) {
      newValue = merge(newValue, {
        componentConfig: {
          PlumaButton: {
            unsafe_withSoftDeprecatedSecondaryVariant: true,
          },
        },
      });
      hasUpdated = true;
    }

    if (hasUpdated) {
      legacyPlumaContextValueStore.set(newValue);
      plumaContextValueStore.set(newValue);
    }
  }, [
    plumaContextValue?.componentConfig?.PlumaButton?.unsafe_withSoftDeprecatedSecondaryVariant,
    plumaContextValue?.componentConfig?.PlumaIcon?.iconNameOverrides?.['design-studio'],
  ]);

  return (
    <PlumaProvider
      theme={resolvedTheme}
      iconManager={plumaContextValue?.iconManager ?? iconManager}
      componentFlags={plumaContextValue?.componentFlags}
      componentConfig={plumaContextValue?.componentConfig}
      shouldApplyCssIsolation={true}
      linkComponent={PlumaLinkDefinition}
      withBaselineStyles={true}
      withGlobalVariables={true}
      withGlobalResetStyles={true}
      globalThemeManager={plumaContextValue?.globalThemeManager ?? globalThemeManager}
      {...props}
    />
  );
}
